import { PayloadAction, createAction, createSlice } from '@reduxjs/toolkit'
import PubNub from 'pubnub'

// `features/` may not import `~/app/auth`, so the action is declared again here.
const auth0Deauthenticate = createAction('auth0/deauthenticate')

/**
 * What a presence signal says about one provider. Every field is optional: a signal carries only
 * what changed, and `updatePresence` sends the same shape.
 */
export interface PresenceState {
  patientId?: number
  typing?: boolean
  viewing?: boolean
}

interface ChatState {
  unreadThreadCount: number
  pubnub: PubNub | null
  // Maps provider IDs to the ID of the patient they are typing to or viewing
  // The source of these data is PubNub events
  typing: { [providerUserId: string]: number | null }
  viewing: { [providerUserId: string]: number | null }
}

const INITIAL_STATE: ChatState = {
  pubnub: null,
  typing: {},
  viewing: {},
  unreadThreadCount: 0,
}

const chatSlice = createSlice({
  name: 'chat',
  initialState: INITIAL_STATE,
  reducers: {
    setPubnubClient: (state, { payload: pubnub }: PayloadAction<PubNub>) => {
      state.pubnub = pubnub
    },
    setPresenceState: (
      state,
      {
        payload: { providerUserId, presenceState },
      }: PayloadAction<{ providerUserId: string; presenceState: PresenceState }>,
    ) => {
      const { patientId, typing, viewing } = presenceState
      // A signal that says "typing" without naming a patient tells us nothing about who, so the
      // map holds null rather than undefined.
      if (typing == true) {
        state.typing[providerUserId] = patientId ?? null
      } else if (typing == false) {
        state.typing[providerUserId] = null
      }
      if (viewing == true) {
        state.viewing[providerUserId] = patientId ?? null
      } else if (viewing == false) {
        state.viewing[providerUserId] = null
      }
    },
  },
  extraReducers: builder => {
    // Unsubscribe from all pubnub events and kill the client
    builder.addCase(auth0Deauthenticate, state => {
      state.pubnub?.destroy()
      return {
        ...state,
        pubnub: null,
      }
    })
  },
})

export default chatSlice
